import { Clock, DollarSign, Layers, Wallet } from 'lucide-react';
import type { PortfolioOverview } from '../App';

type PortfolioHeaderProps = {
  overview: PortfolioOverview;
  fetchedAt: string;
};

export function PortfolioHeader({ overview, fetchedAt }: PortfolioHeaderProps) {
  const cashPct = percentage(overview.cashValue, overview.estimatedNav);
  const quoteAges = overview.holdings
    .map((holding) => quoteAgeSeconds(holding.quoteTime, fetchedAt))
    .filter((age): age is number => age != null && Number.isFinite(age));
  const freshestAge = quoteAges.length > 0 ? Math.min(...quoteAges) : null;
  const staleCount = overview.holdings.filter((holding) => isStaleQuote(holding.quoteTime, holding.staleAfterSeconds, fetchedAt)).length;
  const sourceCount = new Set(overview.holdings.map((holding) => holding.quoteProvider).filter(Boolean)).size;

  return (
    <header className="portfolio-header">
      <div className="portfolio-title">
        <span className="dashboard-kicker">Investment OS</span>
        <h1>Estimated portfolio value</h1>
        <p>
          {freshestAge == null
            ? 'No live quote timestamps are available; broker values are shown where present.'
            : `${sourceCount || 1} quote source${sourceCount === 1 ? '' : 's'} observed, freshest ${formatAge(freshestAge)} old${staleCount ? `, ${staleCount} stale` : ''}.`}
        </p>
      </div>

      <div className="nav-value" aria-label="Estimated portfolio value">
        {formatMoney(overview.estimatedNav)}
      </div>

      <dl className="header-metrics" aria-label="Portfolio summary">
        <div>
          <dt>
            <Wallet size={16} aria-hidden="true" />
            Cash
          </dt>
          <dd>{formatMoney(overview.cashValue)}</dd>
          <span>{formatPercent(cashPct)} staging</span>
        </div>
        <div>
          <dt>
            <Layers size={16} aria-hidden="true" />
            Positions
          </dt>
          <dd>{overview.holdings.length}</dd>
          <span>{formatMoney(overview.positionValue)} invested</span>
        </div>
        <div>
          <dt>
            <DollarSign size={16} aria-hidden="true" />
            Cash/staging
          </dt>
          <dd>{formatPercent(cashPct)}</dd>
          <span>Includes cash balance only</span>
        </div>
        <div>
          <dt>
            <Clock size={16} aria-hidden="true" />
            Updated
          </dt>
          <dd>{formatDateTime(fetchedAt)}</dd>
          <span>Client fetch time</span>
        </div>
      </dl>
    </header>
  );
}

function percentage(value: number, total: number): number {
  if (total <= 0) return 0;
  return (value / total) * 100;
}

function secondsBetween(startIso: string, endIso: string): number {
  const start = Date.parse(startIso);
  const end = Date.parse(endIso);
  if (Number.isNaN(start) || Number.isNaN(end)) return Number.POSITIVE_INFINITY;
  return Math.max(0, Math.floor((end - start) / 1000));
}

function quoteAgeSeconds(quoteTime: string | null, fetchedAt: string): number | null {
  if (!quoteTime) return null;
  return secondsBetween(quoteTime, fetchedAt);
}

function isStaleQuote(quoteTime: string | null, staleAfterSeconds: number | null, fetchedAt: string): boolean {
  const age = quoteAgeSeconds(quoteTime, fetchedAt);
  if (age == null || staleAfterSeconds == null) return false;
  return age > staleAfterSeconds;
}

function formatMoney(value: number): string {
  return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(value);
}

function formatPercent(value: number): string {
  return `${value.toFixed(1)}%`;
}

function formatAge(seconds: number): string {
  if (!Number.isFinite(seconds)) return 'unknown';
  if (seconds < 60) return `${seconds}s`;
  if (seconds < 3600) return `${Math.floor(seconds / 60)}m`;
  return `${Math.floor(seconds / 3600)}h`;
}

function formatDateTime(iso: string): string {
  const date = new Date(iso);
  if (Number.isNaN(date.getTime())) return 'Unknown';
  return new Intl.DateTimeFormat('en-US', {
    month: 'short',
    day: 'numeric',
    hour: 'numeric',
    minute: '2-digit'
  }).format(date);
}
